import { clsx } from 'clsx'
import { useMemo, useState } from 'react'

import { FvButton } from '@fv/client-components'
import type { PropsWithClassName } from '@fv/client-types'

import { Loading } from '@/components/Loading'
import { ShipperSlider } from '@/components/ShipperSlider'

import {
  ShipmentFormSection,
  type ShipmentFormSectionProps,
} from '../shipment-form/ShipmentFormSection'
import { useAccountBrands } from './api'
import { BrandSelectorPanel } from './BrandSelectorPanel'
import type { AccountBranding } from './types'

type Props = PropsWithClassName<{
  value: string | undefined
  onChange: (brandId: string | undefined) => void
}>

export const BrandingSelector = ({ value, onChange, className }: Props) => {
  const [panelOpen, setPanelOpen] = useState(false)
  const { data: brands, isLoading } = useAccountBrands()

  const selectedBrand = useMemo(() => {
    if (!brands?.length) return null
    if (value) return brands.find(b => b._id === value) ?? null
    return brands.find(b => b.isDefault) ?? null
  }, [brands, value])

  const handleSelect = (brand: AccountBranding) => {
    onChange(brand._id)
    setPanelOpen(false)
  }

  if (isLoading) return <Loading />

  return (
    <>
      <div
        className={clsx(
          'flex items-center gap-3 fa-width-auto divide-x divide-fv-gray',
          className,
        )}
      >
        <div className="flex items-center gap-x-3 pr-3">
          {selectedBrand ? (
            <>
              <img
                src={selectedBrand.logoUri}
                alt={selectedBrand.name}
                className="h-16 max-w-48 object-contain border-fv-gray border p-3"
              />
              <span className="inline-block">{selectedBrand.name}</span>
            </>
          ) : (
            <div className="text-fv-gray-dark">No brand selected</div>
          )}
        </div>

        <FvButton
          icon="pen-alt"
          theme="underlined"
          onClick={() => setPanelOpen(true)}
        >
          Change
        </FvButton>
      </div>

      <ShipperSlider
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        className="!p-0"
      >
        <BrandSelectorPanel
          brands={brands ?? []}
          selectedId={value}
          onSelect={handleSelect}
          onClose={() => setPanelOpen(false)}
        />
      </ShipperSlider>
    </>
  )
}

type FormSectionProps = Props & ShipmentFormSectionProps
export const BrandingFormSection = ({
  value,
  onChange,
  ...props
}: FormSectionProps) => {
  const { data: brands, isLoading } = useAccountBrands()
  if (isLoading || (brands?.length ?? 0) <= 1) return null

  return (
    <ShipmentFormSection {...props}>
      <BrandingSelector value={value} onChange={onChange} />
    </ShipmentFormSection>
  )
}
